<template>
  <Modal
    :show="show"
    width="medium"
    hide-default-actions
    @close="show = false"
  >
    <template #activator>
      <div
        @click="show = true"
      >
        Export
      </div>
    </template>

    <template #title>
      <div class="custom-title">
        Save your job in other formats.
      </div>
    </template>

    <template #content>
      <DpplToggleTabs
        v-model="tab"
        :tabs="['Export', 'Copy']"
      >
        <v-tabs-window-item value="Export">
          <div class="d-flex flex-column ga-4 text-textDarkest mt-4">
            <div class="d-flex">
              <v-icon
                icon="material-symbols-light:docs-outline"
                class="my-auto"
                size="20"
              />

              <div class="ml-2 my-auto">
                Export to Microsoft Word
              </div>

              <v-btn
                class="ml-auto"
                variant="flat"
                color="primary"
                rounded="lg"
                height="32"
                @click="onExportWord"
              >
                Export
              </v-btn>
            </div>

            <div class="d-flex">
              <v-icon
                icon="ph:brackets-angle"
                size="20"
              />

              <div class="ml-2 my-auto">
                <div>
                  Export to HTML
                </div>
                <div class="mt-1 text-caption opacity-70">
                  Works well with Microsoft Word, Google Docs and other apps.
                </div>
              </div>

              <v-btn
                class="ma-auto"
                variant="outlined"
                color="primary"
                rounded="lg"
                height="32"
                @click="onExportHtml"
              >
                Export
              </v-btn>
            </div>

            <div class="d-flex">
              <v-icon
                icon="material-symbols-light:tag"
                size="20"
              />

              <div class="ml-2 my-auto">
                <div>
                  Export to Markdown
                </div>
                <div class="mt-1 text-caption opacity-70">
                  Works well with Notion, Github and other apps.
                </div>
              </div>

              <v-btn
                class="ma-auto"
                variant="outlined"
                color="primary"
                rounded="lg"
                height="32"
                @click="onExportMarkdown"
              >
                Export
              </v-btn>
            </div>
          </div>
        </v-tabs-window-item>

        <v-tabs-window-item value="Copy">
          <div class="d-flex flex-column ga-4 text-textDarkest mt-4">
            <div class="d-flex">
              <div class="my-auto">
                Copy All Sections
              </div>

              <v-btn
                class="my-auto ml-auto"
                variant="flat"
                color="primary"
                rounded="lg"
                height="32"
                :disabled="lastCopied === 'all'"
                @click="onCopyAllFieldsClick"
              >
                Copy All
              </v-btn>
            </div>

            <div class="d-flex">
              <div class="my-auto">
                <div>
                  Copy Title
                </div>
              </div>

              <v-btn
                class="my-auto ml-auto"
                variant="outlined"
                color="primary"
                rounded="lg"
                height="32"
                :disabled="lastCopied === 'title'"
                @click="copyTitle"
              >
                Copy Title
              </v-btn>
            </div>

            <div
              v-for="field in fieldsWithFooterAndHeaderJoined"
              :key="field.id"
              class="d-flex"
            >
              <div class="my-auto">
                <div>
                  Copy {{ field.label }}
                </div>
              </div>

              <v-btn
                class="my-auto ml-auto"
                variant="outlined"
                color="primary"
                rounded="lg"
                height="32"
                :disabled="lastCopied === field.id"
                @click="copyField(field.id, field.label)"
              >
                Copy
              </v-btn>
            </div>
          </div>
        </v-tabs-window-item>
      </DpplToggleTabs>
    </template>
  </Modal>
</template>

<script setup>
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useDocumentEditor } from '@/stores/document/editor';
import { useEditor } from '@/stores/editor/editor';
import { joinFooterAndHeaderFields } from '@/helpers/headerFooterFields';
import { shouldHideIfEmpty } from '@/helpers/constants/hideEmptyFields';
import Modal from '@/components/shared/Modal.vue';
import DpplToggleTabs from '@ui/components/general/DpplToggleTabs/DpplToggleTabs.vue';

const props = defineProps({
  jobId: { type: [String, Number], default: '' },
  title: { type: String, default: '' },
  isDataLoaded: { type: Boolean, default: false },
});

const emits = defineEmits(['tracking-event']);

const show = ref(false);
const tab = ref('Export');
const lastCopied = ref('');

const editorStore = useEditor();
const documentEditorStore = useDocumentEditor();
const { copyFieldToClipboard, copyAllFieldsToClipboard, copyToClipboard } = editorStore;
const { fields } = storeToRefs(documentEditorStore);

const shareKeyParam = computed(() => {
  const { shareKey } = window.sessionStorage;
  return shareKey ? `&key=${shareKey}` : '';
});

const fieldsWithFooterAndHeaderJoined = computed(() => joinFooterAndHeaderFields(fields.value)
  .filter((field) => !shouldHideIfEmpty({
    type: field.type,
    sections: field.sections,
    isTemplate: false,
  }))
  .filter((field) => field.html.length > 20)); // no copy button for empty field

const copyTitle = () => {
  copyToClipboard({ content: props.title });
  emits('tracking-event', { action: 'copyTitle', args: { job: props } });
  lastCopied.value = 'title';
};

const copyField = (id, field) => {
  copyFieldToClipboard(id);
  emits('tracking-event', { action: 'copyContent', args: { job: props, props: { field } } });
  lastCopied.value = id;
};

const onCopyAllFieldsClick = () => {
  copyAllFieldsToClipboard(props.title);
  emits('tracking-event', { action: 'copyAllFields', args: { job: props } });
  lastCopied.value = 'all';
};

const onExportWord = () => {
  emits('tracking-event', {
    action: 'editorLinkClick',
    args: { label: 'Export: Word' },
  });

  window.open(`/recruit/download?id=${props.jobId}&format=word${shareKeyParam.value}`);
};

const onExportHtml = () => {
  emits('tracking-event', {
    action: 'editorLinkClick',
    args: { label: 'Export: HTML' },
  });

  window.open(`/recruit/download?id=${props.jobId}&format=html${shareKeyParam.value}`);
};

const onExportMarkdown = () => {
  emits('tracking-event', {
    action: 'editorLinkClick',
    args: { label: 'Export: Markdown' },
  });

  window.open(`/recruit/download?id=${props.jobId}&format=markdown${shareKeyParam.value}`);
};
</script>

<style scoped lang="less">
.custom-title {
  color: fade(@grey-900, 70%);
  font-size: 13px;
  font-weight: 500;
}
</style>
